/**
 * Shared wizard state + FormData builder for POST api/owner/create-asset (multipart).
 * Adjust field names to match backend if the API contract differs.
 */

export function initialAssetDraft() {
  return {
    categoryId: null,
    categoryTitle: "",
    assetName: "",
    description: "",
    streetAddress: "",
    country: "",
    /** Optional Mongo ids when using location API; else `country` string is sent */
    addressCountryId: "",
    addressStateId: "",
    addressCityId: "",
    state: "",
    city: "",
    zipCode: "",
    propertyType: "",
    sizeValue: "",
    sizeUnit: "sq ft",
    bedrooms: "",
    bathrooms: "",
    parkingSpaces: "",
    yearBuilt: "",
    amenityIds: [],
    /** RERA / registration details (sent under `regaDetails` in API payload) */
    regaDeedNumber: "",
    regaCrNumber: "",
    regaOwnerName: "",
    /** REGA certificate file → multipart key `regaCertificate` */
    regaCertificateFile: null,
    mainImage: null,
    galleryImages: [],
    spvName: "",
    registrationNumber: "",
    jurisdiction: "",
    structureType: "",
    formationDate: "",
    registeredAddress: "",
    trn: "",
    authorizedSignatoryFile: null,
    licenseExpiryDate: "",
    purpose: "",
    spvCertificateFiles: [],
    totalAssetValue: "",
    /** SAR per token; supply = floor(totalAssetValue / pricePerTokenSar) */
    pricePerTokenSar: "10",
    expectedRoi: "",
    rentalYield: "",
    dividendFrequency: "",
    valuationReport: null,
    ownershipProof: null,
    legalDocuments: null,
    blockchainNetwork: "ethereum",
    /** Default filled in Token Configuration as 2× price per token (Step 5) */
    minimumInvestmentSar: "",
    fractionalOwnership: true,
    /** Non–Real Estate: Renewable Energy */
    energyType: "",
    installedCapacityValue: "",
    installedCapacityUnit: "MW",
    annualProductionMwh: "",
    projectLocationSite: "",
    /** Non–Real Estate: Commodities */
    commodityType: "",
    commodityQuantityValue: "",
    commodityQuantityUnit: "kg",
    purityOrGrade: "",
    storageLocationVault: "",
    /** Non–Real Estate: Luxury Goods */
    luxuryBrand: "",
    luxuryModel: "",
    luxurySerialNumber: "",
    luxuryYearOfManufacture: "",
    luxuryCondition: "",
  };
}

export function parseAmount(str) {
  const n = parseFloat(String(str).replace(/,/g, "").replace(/^SAR\s*/i, "").trim());
  return Number.isFinite(n) ? n : 0;
}

function fileLooksLikeImage(file) {
  const type = typeof file.type === "string" ? file.type : "";
  if (type.startsWith("image/")) return true;
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(String(file.name || ""));
}

function fileLooksLikePdf(file) {
  const type = typeof file.type === "string" ? file.type : "";
  if (type === "application/pdf") return true;
  return /\.pdf$/i.test(String(file.name || ""));
}

/** Parse user input to a finite number (commas / % / SAR stripped). Never returns a string. */
function toNumber(str) {
  const n = parseFloat(
    String(str ?? "")
      .replace(/,/g, "")
      .replace(/^SAR\s*/i, "")
      .replace(/%/g, "")
      .trim()
  );
  return Number.isFinite(n) ? n : undefined;
}

function toPercentNumber(str) {
  const n = parseFloat(String(str ?? "").replace(/%/g, "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : undefined;
}

function toSizeSqFt(str) {
  const n = parseFloat(String(str ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : undefined;
}

function toOptionalInt(str) {
  const s = String(str ?? "").trim();
  if (!s) return undefined;
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Remove undefined, null, and "". Preserve numbers (including 0) and booleans (including false).
 * Drop empty nested plain objects.
 */
function deepClean(value) {
  if (value === undefined || value === null || value === "") return undefined;
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (Array.isArray(value)) {
    return value.map(deepClean).filter((v) => v !== undefined && v !== null && v !== "");
  }
  if (typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      const c = deepClean(v);
      if (c === undefined) continue;
      out[k] = c;
    }
    return Object.keys(out).length ? out : undefined;
  }
  return value;
}

/** Map amenity id → label for review (subset of Step2 AMENITIES) */
export const AMENITY_ID_TO_LABEL = {
  pool: "Swimming Pool",
  concierge: "Concierge Service",
  bbq: "BBQ Area",
  metro: "Metro Access",
  covered_parking: "Covered Parking",
  gym: "Gym & Spa",
  underground_parking: "Underground Parking",
  marina: "Marina View",
  elevators: "High-Speed Elevators",
  garden: "Garden",
  security: "24/7 Security",
  kids: "Kids Play Area",
  beach: "Beach Access",
  lounge: "Business Lounge",
  rooftop: "Rooftop Terrace",
};

const BLOCKCHAIN_LABEL = {
  xrpl: "XRPL",
  ethereum: "Ethereum",
  polygon: "Polygon",
};

/**
 * If `raw` matches a key in `optionsMap` (from assetCreationData), return the human label; else return trimmed string.
 * Ensures multipart JSON sends display values (e.g. "sq ft") not enum keys (e.g. "SQ_FT").
 */
function resolveCreationOptionLabel(raw, optionsMap) {
  if (raw === undefined || raw === null || raw === "") return undefined;
  const s = String(raw).trim();
  if (!s) return undefined;
  if (optionsMap && typeof optionsMap === "object" && !Array.isArray(optionsMap) && optionsMap[s]) {
    return String(optionsMap[s]).trim();
  }
  return s;
}

/** Backend Joi: documentation.*.fileType must be "pdf" | "zip" only. */
function documentationFileType(file) {
  if (!file || typeof file !== "object") return "pdf";
  const name = String(file.name || "").toLowerCase();
  const mime = String(file.type || "").toLowerCase();
  if (name.endsWith(".zip") || mime === "application/zip" || mime === "application/x-zip-compressed") {
    return "zip";
  }
  if (name.endsWith(".pdf") || mime === "application/pdf") {
    return "pdf";
  }
  // Images / other uploads: enum allows only pdf | zip — use pdf for metadata (prefer real PDFs for compliance).
  return "pdf";
}

/**
 * Nested JSON body for create/update (after `deepClean`), before multipart wrapping.
 *
 * @param {object} [creationData] — merged output of `mergeAssetCreationData`
 */
export function buildCreateAssetDataPayload(draft, creationData) {
  const cd = creationData && typeof creationData === "object" ? creationData : {};

  const valuation = toNumber(draft.totalAssetValue) ?? 0;
  const minInvFromDraft = toNumber(draft.minimumInvestmentSar) ?? 0;
  const pricePerTokenRaw =
    draft.pricePerTokenSar == null ? "10" : String(draft.pricePerTokenSar);
  const pricePerTokenFromDraft = toNumber(pricePerTokenRaw);
  const priceForDefaultMin =
    pricePerTokenFromDraft !== undefined && pricePerTokenFromDraft > 0
      ? pricePerTokenFromDraft
      : 10;
  const minInv =
    minInvFromDraft > 0 ? minInvFromDraft : 2 * priceForDefaultMin;
  const pricePerToken =
    pricePerTokenFromDraft !== undefined && pricePerTokenFromDraft > 0
      ? pricePerTokenFromDraft
      : minInvFromDraft > 0
        ? minInvFromDraft
        : 0;
  const computedTotalSupply =
    valuation > 0 && pricePerToken > 0 ? Math.floor(valuation / pricePerToken) : 0;
  const totalSupply = computedTotalSupply >= 1 ? computedTotalSupply : 1;

  const featureLabels = (draft.amenityIds || [])
    .map((id) => {
      if (typeof id === "string" && id.trim()) {
        return AMENITY_ID_TO_LABEL[id] || id.trim();
      }
      return null;
    })
    .filter(Boolean);

  const sizeSqFt = toSizeSqFt(draft.sizeValue);
  const sizeUnitLabel = resolveCreationOptionLabel(draft.sizeUnit, cd.sizeUnitOptions);
  const isRealEstate = String(draft.categoryTitle || "").trim() === "Real Estate";
  const catTitle = String(draft.categoryTitle || "").trim();
  const isRenewableEnergy = catTitle === "Renewable Energy";
  const isCommodities = catTitle === "Commodities";
  const isLuxuryGoods = catTitle === "Luxury Goods";

  const installedCapNum = toNumber(draft.installedCapacityValue);
  const annualMwhNum = toNumber(draft.annualProductionMwh);
  const commodityQtyNum = toNumber(draft.commodityQuantityValue);

  const propertySpecifications = isRealEstate
    ? {
        ...(draft.propertyType ? { propertyType: String(draft.propertyType).trim() } : {}),
        ...(sizeSqFt !== undefined ? { sizeSqFt: Number(sizeSqFt) } : {}),
        ...(sizeUnitLabel ? { sizeUnit: sizeUnitLabel } : {}),
        ...(toOptionalInt(draft.bedrooms) !== undefined
          ? { bedrooms: toOptionalInt(draft.bedrooms) }
          : {}),
        ...(toOptionalInt(draft.bathrooms) !== undefined
          ? { bathrooms: toOptionalInt(draft.bathrooms) }
          : {}),
        ...(toOptionalInt(draft.parkingSpaces) !== undefined
          ? { parkingSpaces: toOptionalInt(draft.parkingSpaces) }
          : {}),
        ...(toOptionalInt(draft.yearBuilt) !== undefined
          ? { yearBuilt: toOptionalInt(draft.yearBuilt) }
          : {}),
      }
    : isRenewableEnergy
      ? {
          ...(draft.energyType ? { energyType: String(draft.energyType).trim() } : {}),
          ...(installedCapNum !== undefined && installedCapNum > 0
            ? { installedCapacity: Number(installedCapNum) }
            : {}),
          ...(draft.installedCapacityUnit
            ? { installedCapacityUnit: String(draft.installedCapacityUnit).trim() }
            : {}),
          ...(annualMwhNum !== undefined && annualMwhNum > 0
            ? { annualProductionMWh: Number(annualMwhNum) }
            : {}),
          ...(draft.projectLocationSite
            ? { projectLocationSite: String(draft.projectLocationSite).trim() }
            : {}),
        }
      : {};

  /** API: basicInfo.commodityAssetDetails — grade, location (not propertySpecifications) */
  const commodityAssetDetailsRaw = isCommodities
    ? {
        ...(draft.commodityType ? { commodityType: String(draft.commodityType).trim() } : {}),
        ...(commodityQtyNum !== undefined && commodityQtyNum > 0
          ? { commodityQuantity: Number(commodityQtyNum) }
          : {}),
        ...(draft.purityOrGrade ? { grade: String(draft.purityOrGrade).trim() } : {}),
        ...(draft.storageLocationVault
          ? { location: String(draft.storageLocationVault).trim() }
          : {}),
      }
    : null;
  const commodityAssetDetails =
    commodityAssetDetailsRaw && Object.keys(commodityAssetDetailsRaw).length
      ? commodityAssetDetailsRaw
      : null;

  /** API expects `model` as string (e.g. "2025" or "Nautilus 5711/1A") */
  const luxuryModelStr = String(draft.luxuryModel ?? "").trim();

  /** API: basicInfo.luxuryAssetDetails */
  const luxuryAssetDetailsRaw = isLuxuryGoods
    ? {
        ...(draft.luxuryBrand ? { brand: String(draft.luxuryBrand).trim() } : {}),
        ...(luxuryModelStr ? { model: luxuryModelStr } : {}),
        ...(draft.luxurySerialNumber
          ? { serialNumber: String(draft.luxurySerialNumber).trim() }
          : {}),
        ...(String(draft.luxuryYearOfManufacture ?? "").trim()
          ? { manufacturedYear: String(draft.luxuryYearOfManufacture).trim() }
          : {}),
        ...(draft.luxuryCondition ? { condition: String(draft.luxuryCondition).trim() } : {}),
      }
    : null;
  const luxuryAssetDetails =
    luxuryAssetDetailsRaw && Object.keys(luxuryAssetDetailsRaw).length
      ? luxuryAssetDetailsRaw
      : null;

  const siteLine = String(draft.projectLocationSite || "").trim();
  const streetLine = String(draft.streetAddress || "").trim();
  const primaryStreet = streetLine || (isRenewableEnergy ? siteLine : "");

  const completeAddress = {
    ...(primaryStreet ? { streetAddress: primaryStreet } : {}),
    ...(draft.addressCountryId || draft.country
      ? {
          country: draft.addressCountryId
            ? String(draft.addressCountryId).trim()
            : String(draft.country).trim(),
        }
      : {}),
    ...(draft.addressStateId || draft.state
      ? {
          state: draft.addressStateId
            ? String(draft.addressStateId).trim()
            : String(draft.state).trim(),
        }
      : {}),
    ...(draft.addressCityId || draft.city
      ? {
          city: draft.addressCityId
            ? String(draft.addressCityId).trim()
            : String(draft.city).trim(),
        }
      : {}),
    ...(draft.zipCode ? { zipCode: String(draft.zipCode).trim() } : {}),
  };

  const basicInfoCore = {
    ...(draft.assetName ? { assetName: String(draft.assetName).trim() } : {}),
    ...(draft.description ? { description: String(draft.description).trim() } : {}),
  };

  const basicInfo = isCommodities
    ? {
        ...basicInfoCore,
        ...(commodityAssetDetails ? { commodityAssetDetails } : {}),
      }
    : isLuxuryGoods
      ? {
          ...basicInfoCore,
          ...(luxuryAssetDetails ? { luxuryAssetDetails } : {}),
        }
      : {
          ...basicInfoCore,
          completeAddress,
          propertySpecifications,
          featuresAndAmenities: featureLabels,
        };

  const structureTypeLabel = resolveCreationOptionLabel(
    draft.structureType,
    cd.spvStructureTypeOptions
  );

  const spvDetails = {
    ...(draft.spvName ? { spvName: String(draft.spvName).trim() } : {}),
    ...(draft.registrationNumber
      ? { registrationNumber: String(draft.registrationNumber).trim() }
      : {}),
    ...(draft.jurisdiction ? { jurisdiction: String(draft.jurisdiction).trim() } : {}),
    ...(draft.registeredAddress
      ? { registeredAddress: String(draft.registeredAddress).trim() }
      : {}),
    ...(draft.trn ? { taxRegistrationNumber: String(draft.trn).trim() } : {}),
    ...(draft.formationDate ? { formationDate: String(draft.formationDate).trim() } : {}),
    ...(structureTypeLabel ? { structureType: structureTypeLabel } : {}),
    ...(draft.licenseExpiryDate
      ? { licenseExpiryDate: String(draft.licenseExpiryDate).trim() }
      : {}),
    ...(draft.purpose ? { purpose: String(draft.purpose).trim() } : {}),
  };

  const rentalYieldNum = toPercentNumber(draft.rentalYield);
  const expectedRoiNum = toPercentNumber(draft.expectedRoi);

  const financialDetails = {
    ...(valuation > 0 ? { totalAssetValue: Number(valuation) } : {}),
    ...(rentalYieldNum !== undefined ? { rentalYield: Number(rentalYieldNum) } : {}),
    ...(expectedRoiNum !== undefined ? { expectedROI: Number(expectedRoiNum) } : {}),
    ...(minInv > 0 ? { minimumInvestment: Number(minInv) } : {}),
    ...(draft.dividendFrequency
      ? { dividendFrequency: String(draft.dividendFrequency).trim() }
      : {}),
  };

  const blockchainLabel =
    BLOCKCHAIN_LABEL[draft.blockchainNetwork] ||
    (draft.blockchainNetwork ? String(draft.blockchainNetwork) : undefined);

  const tokenEconomics = {
    ...(pricePerToken > 0 ? { tokenPriceUSD: Number(pricePerToken) } : {}),
    totalSupply: Number(totalSupply),
  };

  const tokenConfiguration = {
    ...(blockchainLabel ? { blockchainNetwork: blockchainLabel } : {}),
    ...(minInv > 0 ? { minimumInvestment: Number(minInv) } : {}),
    fractionalOwnership: draft.fractionalOwnership !== false,
  };

  const regaDetails = {
    ...(draft.regaDeedNumber ? { deedNumber: String(draft.regaDeedNumber).trim() } : {}),
    ...(draft.regaCrNumber ? { crNumber: String(draft.regaCrNumber).trim() } : {}),
    ...(draft.regaOwnerName ? { ownerName: String(draft.regaOwnerName).trim() } : {}),
  };

  const documentation = {};
  if (draft.ownershipProof) {
    documentation.titleDeed = { fileType: documentationFileType(draft.ownershipProof) };
  }
  if (draft.legalDocuments) {
    documentation.legalDocuments = [{ fileType: documentationFileType(draft.legalDocuments) }];
  }

  const dataPayload = {
    ...(draft.categoryId ? { assetCategory: String(draft.categoryId).trim() } : {}),
    basicInfo,
    spvDetails,
    financialDetails,
    tokenEconomics,
    tokenConfiguration,
    regaDetails,
    ...(Object.keys(documentation).length > 0 ? { documentation } : {}),
  };

  return deepClean(dataPayload) ?? {};
}

/**
 * API schema (POST /api/owner/create-asset, multipart):
 * - `data`: Text — one JSON string (nested object below).
 * - Files: mainImage, spvSignature, spvDocuments (repeat), valuationReport, galleryImages (repeat),
 *   titleDeed (proof of ownership / deed), regaCertificate, legalDocuments (legal & compliance).
 *
 * Nested `data` shape:
 * assetCategory, basicInfo, spvDetails, financialDetails, tokenEconomics (totalSupply required), tokenConfiguration, regaDetails,
 * documentation: { titleDeed: { fileType }, legalDocuments: [{ fileType }] } — fileType pdf | zip, aligned with multipart files
 * All numeric fields must be JSON numbers (not comma strings).
 *
 * @param {object} [creationData] — merged output of `mergeAssetCreationData` (maps like sizeUnitOptions, spvStructureTypeOptions) so enum keys are serialized as labels.
 * @param {{ forUpdate?: boolean }} [options] — when `forUpdate`, only append multipart file fields for new `File` uploads (omit unchanged blobs).
 */
export function buildCreateAssetFormData(draft, creationData, options = {}) {
  const { forUpdate = false } = options;

  const fd = new FormData();
  const cleaned = buildCreateAssetDataPayload(draft, creationData);
  fd.append("data", JSON.stringify(cleaned ?? {}));

  const appendFile = (fieldName, file) => {
    if (!file) return;
    if (forUpdate && !(file instanceof File)) return;
    if (fieldName === "regaCertificate") {
      const name = typeof file.name === "string" && file.name ? file.name : "regaCertificate";
      fd.append(fieldName, file, name);
      return;
    }
    fd.append(fieldName, file);
  };

  appendFile("mainImage", draft.mainImage);
  appendFile("spvSignature", draft.authorizedSignatoryFile);
  (draft.spvCertificateFiles || []).forEach((file) => {
    appendFile("spvDocuments", file);
  });
  appendFile("valuationReport", draft.valuationReport);
  (draft.galleryImages || []).forEach((file) => {
    appendFile("galleryImages", file);
  });
  appendFile("regaCertificate", draft.regaCertificateFile);
  appendFile("titleDeed", draft.ownershipProof);
  appendFile("legalDocuments", draft.legalDocuments);

  return fd;
}

const UPDATE_PAYLOAD_SECTION_KEYS = [
  "assetCategory",
  "basicInfo",
  "spvDetails",
  "financialDetails",
  "tokenEconomics",
  "tokenConfiguration",
  "regaDetails",
  "documentation",
];

/**
 * PATCH multipart: `data` JSON contains only sections that differ from `baselineDraft`,
 * plus multipart parts only for new `File` uploads on `currentDraft`.
 *
 * When a new `regaCertificate` file is included, `data` always includes `regaDetails`
 * (deed/cr/owner) so the server receives `{"regaDetails":{...}}` alongside the binary file part.
 *
 * @returns {{ formData: FormData; isEmpty: boolean }}
 */
export function buildPartialUpdateAssetFormData(currentDraft, baselineDraft, creationData) {
  const next = buildCreateAssetDataPayload(currentDraft, creationData);
  const prev = buildCreateAssetDataPayload(baselineDraft, creationData);

  const partial = {};
  for (const key of UPDATE_PAYLOAD_SECTION_KEYS) {
    const a = next[key];
    const b = prev[key];
    const sa = JSON.stringify(a === undefined ? null : a);
    const sb = JSON.stringify(b === undefined ? null : b);
    if (sa !== sb && a !== undefined) {
      partial[key] = a;
    }
  }

  const fd = new FormData();
  let hasNewFiles = false;
  let appendedRegaCertificate = false;

  const appendIfNewFile = (fieldName, file) => {
    if (!file || !(file instanceof File)) return;
    if (fieldName === "regaCertificate") {
      const name = typeof file.name === "string" && file.name ? file.name : "regaCertificate";
      fd.append(fieldName, file, name);
      appendedRegaCertificate = true;
    } else {
      fd.append(fieldName, file);
    }
    hasNewFiles = true;
  };

  appendIfNewFile("mainImage", currentDraft.mainImage);
  appendIfNewFile("spvSignature", currentDraft.authorizedSignatoryFile);
  (currentDraft.spvCertificateFiles || []).forEach((file) => {
    appendIfNewFile("spvDocuments", file);
  });
  appendIfNewFile("valuationReport", currentDraft.valuationReport);
  (currentDraft.galleryImages || []).forEach((file) => {
    appendIfNewFile("galleryImages", file);
  });
  appendIfNewFile("regaCertificate", currentDraft.regaCertificateFile);
  appendIfNewFile("titleDeed", currentDraft.ownershipProof);
  appendIfNewFile("legalDocuments", currentDraft.legalDocuments);

  if (appendedRegaCertificate) {
    partial.regaDetails = next.regaDetails ?? {};
  }

  const hasJsonChanges = Object.keys(partial).length > 0;
  if (hasJsonChanges) {
    fd.append("data", JSON.stringify(partial));
  } else if (hasNewFiles) {
    fd.append("data", JSON.stringify({}));
  }

  const isEmpty = !hasJsonChanges && !hasNewFiles;
  return { formData: fd, isEmpty };
}

/**
 * @param {object} [options]
 * @param {Record<string, string>} [options.spvStructureTypeOptions] — map enum key → label for review
 */
export function buildReviewModel(draft, options = {}) {
  const spvStructureTypeOptions = options.spvStructureTypeOptions || {};
  const valuation = parseAmount(draft.totalAssetValue);
  const minInvFromDraft = parseAmount(draft.minimumInvestmentSar);
  const priceFromStep = parseAmount(
    draft.pricePerTokenSar == null ? "10" : String(draft.pricePerTokenSar)
  );
  const priceBasis = priceFromStep > 0 ? priceFromStep : 10;
  const minInv = minInvFromDraft > 0 ? minInvFromDraft : 2 * priceBasis;
  const price = priceBasis;
  const supply = valuation > 0 && price > 0 ? Math.floor(valuation / price) : 0;

  const fmtSar = (n) =>
    new Intl.NumberFormat("en-SA", { maximumFractionDigits: 0 }).format(n) + " SAR";

  const featureLabels = (draft.amenityIds || [])
    .map((id) => {
      if (typeof id === "string" && id.trim()) {
        return AMENITY_ID_TO_LABEL[id] || id.trim();
      }
      return null;
    })
    .filter(Boolean);

  /**
   * Each uploaded file as its own row for review step. Reuse the same object URL as the hero /
   * gallery strip where possible so thumbnails stay valid and we do not double-revoke blobs.
   */
  const docs = [];

  const heroImageUrl = draft.mainImage ? URL.createObjectURL(draft.mainImage) : null;
  if (draft.mainImage?.name) {
    const isImage = fileLooksLikeImage(draft.mainImage);
    docs.push({
      label: "Main image (hero)",
      file: draft.mainImage.name,
      previewUrl: heroImageUrl,
      fileType: isImage ? "image" : "other",
      hint: null,
    });
  }

  const galleryFiles = (draft.galleryImages || []).filter(Boolean);
  const galleryThumbs = galleryFiles.map((f) => URL.createObjectURL(f));
  galleryFiles.forEach((f, i) => {
    const isImage = fileLooksLikeImage(f);
    const isPdf = fileLooksLikePdf(f);
    let fileType = "other";
    if (isImage) fileType = "image";
    else if (isPdf) fileType = "pdf";
    docs.push({
      label: `Gallery image ${i + 1}`,
      file: f.name,
      previewUrl: galleryThumbs[i] ?? null,
      fileType,
      hint: null,
    });
  });

  function pushDoc(label, file, { withPreview = true, hint = null } = {}) {
    if (!file || !file.name) return;
    const isImage = fileLooksLikeImage(file);
    const isPdf = fileLooksLikePdf(file);
    let previewUrl = null;
    let fileType = "other";
    if (isImage) fileType = "image";
    else if (isPdf) fileType = "pdf";
    if (withPreview && (isImage || isPdf)) {
      try {
        previewUrl = URL.createObjectURL(file);
      } catch {
        previewUrl = null;
      }
    }
    docs.push({ label, file: file.name, previewUrl, fileType, hint });
  }

  (draft.spvCertificateFiles || []).forEach((f, i) => {
    pushDoc(i === 0 ? "SPV certificate" : "SPV document", f, { withPreview: true });
  });
  pushDoc("Valuation report", draft.valuationReport, { withPreview: true });
  pushDoc("REGA certificate", draft.regaCertificateFile, { withPreview: true });
  pushDoc("Title deed (proof of ownership)", draft.ownershipProof, { withPreview: true });
  pushDoc("Legal & compliance documents", draft.legalDocuments, { withPreview: true });
  pushDoc("SPV signature (authorized signatory)", draft.authorizedSignatoryFile, {
    withPreview: true,
  });

  return {
    assetName: draft.assetName || "—",
    category: draft.categoryTitle || "—",
    streetAddress: draft.streetAddress || "—",
    location:
      [draft.city, draft.state, draft.country].filter((x) => x && String(x).trim()).join(", ") ||
      draft.country ||
      "—",
    heroImage: heroImageUrl,
    galleryThumbs,
    description: draft.description || "—",
    propertyType: draft.propertyType || "—",
    size: [draft.sizeValue, draft.sizeUnit].filter(Boolean).join(" ") || "—",
    feature: featureLabels[0] || "—",
    featureList: featureLabels,
    spv: {
      name: draft.spvName || "—",
      licenseType:
        (draft.structureType && spvStructureTypeOptions[draft.structureType]) ||
        draft.structureType ||
        "—",
      purpose: draft.purpose || "—",
      registrationNumber: draft.registrationNumber || "—",
      formationDate: draft.formationDate || "—",
      jurisdiction: draft.jurisdiction || "—",
      signatoryFile: draft.authorizedSignatoryFile?.name || "—",
    },
    financial: {
      totalValuation: valuation > 0 ? fmtSar(valuation) : "—",
      expectedRoi: (() => {
        const r = draft.expectedRoi;
        if (r === undefined || r === null || String(r).trim() === "") return "—";
        const s = String(r).trim();
        return s.includes("%") ? s : `${s}%`;
      })(),
      rentalYield: (() => {
        const y = draft.rentalYield;
        if (y === undefined || y === null || String(y).trim() === "") return "—";
        const s = String(y).trim();
        return s.includes("%") ? s : `${s}%`;
      })(),
      minInvestment: minInv ? fmtSar(minInv) : "—",
    },
    token: {
      totalSupply: `${supply.toLocaleString("en-SA")} Tokens`,
      pricePerToken: fmtSar(price),
      blockchain: BLOCKCHAIN_LABEL[draft.blockchainNetwork] || draft.blockchainNetwork,
      fractional: draft.fractionalOwnership ? "Enabled" : "Disabled",
    },
    documents: docs,
  };
}
